// Overlay global de búsqueda de ventas. Se abre con Cmd/Ctrl+K desde
// cualquier pantalla y al elegir un resultado navega a Ventas con el
// drawer del deal abierto.
import { useState, useEffect, useRef, fmt, Ic } from './common.jsx';

export function GlobalSearch({onSelect, onClose}) {
  const [q, setQ] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [highlight, setHighlight] = useState(0);
  const inputRef = useRef(null);

  // Focus + Esc para cerrar
  useEffect(() => {
    inputRef.current?.focus();
    const handler = (e) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  // Búsqueda con debounce
  useEffect(() => {
    const term = q.trim();
    if (term.length < 2) { setResults([]); setLoading(false); return; }
    setLoading(true);
    const t = setTimeout(async () => {
      try {
        const r = await fetch('/api/search/sales?q=' + encodeURIComponent(term));
        const data = await r.json();
        setResults(Array.isArray(data) ? data : []);
      } catch {
        setResults([]);
      } finally {
        setLoading(false);
        setHighlight(0);
      }
    }, 180);
    return () => clearTimeout(t);
  }, [q]);

  const onKey = (e) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setHighlight(h => Math.min(h+1, results.length-1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setHighlight(h => Math.max(h-1, 0)); }
    else if (e.key === 'Enter' && results[highlight]) { onSelect(results[highlight]); }
  };

  return (
    <div onClick={onClose}
      style={{
        position:'fixed',inset:0,background:'rgba(20,20,20,.45)',zIndex:500,
        display:'flex',alignItems:'flex-start',justifyContent:'center',
        paddingTop:'10vh',backdropFilter:'blur(2px)'
      }}>
      <div onClick={e=>e.stopPropagation()}
        style={{
          background:'var(--wh)',borderRadius:14,width:'min(640px,94vw)',
          boxShadow:'0 16px 48px rgba(0,0,0,.25)',overflow:'hidden',
          display:'flex',flexDirection:'column',maxHeight:'70vh'
        }}>
        <div style={{display:'flex',alignItems:'center',gap:12,padding:'14px 18px',borderBottom:'1px solid var(--bds)'}}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{width:18,height:18,color:'var(--tx-mu)',flexShrink:0}}>
            <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
          </svg>
          <input ref={inputRef} value={q} onChange={e=>setQ(e.target.value)} onKeyDown={onKey}
            placeholder="Buscar venta por nombre, alumno, comercial o ID…"
            style={{
              flex:1,fontSize:15,padding:'4px 0',border:'none',outline:'none',
              fontFamily:'inherit',color:'var(--tx)',background:'transparent'
            }}/>
          <button className="btn btn-gh btn-sm" onClick={onClose}>{Ic.close}</button>
        </div>
        <div style={{flex:1,overflowY:'auto'}}>
          {q.trim().length < 2 ? (
            <div style={{padding:'24px 18px',fontSize:12,color:'var(--tx-mu)',textAlign:'center',lineHeight:1.6}}>
              Escribe al menos 2 caracteres.<br/>
              <span style={{fontSize:11,opacity:.7}}>Tip: <kbd style={{padding:'1px 6px',background:'var(--bg)',border:'1px solid var(--bds)',borderRadius:4,fontFamily:'inherit',fontSize:10}}>↑↓</kbd> navegar · <kbd style={{padding:'1px 6px',background:'var(--bg)',border:'1px solid var(--bds)',borderRadius:4,fontFamily:'inherit',fontSize:10}}>Enter</kbd> abrir · <kbd style={{padding:'1px 6px',background:'var(--bg)',border:'1px solid var(--bds)',borderRadius:4,fontFamily:'inherit',fontSize:10}}>Esc</kbd> cerrar</span>
            </div>
          ) : loading ? (
            <div style={{padding:'18px',fontSize:12,color:'var(--tx-mu)',textAlign:'center'}}>Buscando…</div>
          ) : results.length === 0 ? (
            <div style={{padding:'18px',fontSize:12,color:'var(--tx-mu)',textAlign:'center'}}>Sin resultados.</div>
          ) : (
            results.map((r, i) => (
              <div key={r.dealId}
                onClick={() => onSelect(r)}
                onMouseEnter={() => setHighlight(i)}
                style={{
                  padding:'10px 18px',borderBottom:'1px solid var(--bds)',cursor:'pointer',
                  background: i===highlight ? 'var(--pu-bgl)' : 'transparent',
                  borderLeft: i===highlight ? '3px solid var(--pu)' : '3px solid transparent'
                }}>
                <div style={{display:'flex',alignItems:'baseline',justifyContent:'space-between',gap:10}}>
                  <div style={{fontSize:13,fontWeight:600,color:'var(--tx)',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap',minWidth:0,flex:1}}>
                    {r.name}
                  </div>
                  <div style={{fontSize:13,fontWeight:600,color:'var(--pu)',flexShrink:0}}>{fmt(r.amount)}</div>
                </div>
                <div style={{fontSize:11,color:'var(--tx-mu)',marginTop:2,display:'flex',gap:8,flexWrap:'wrap'}}>
                  {r.contactName && <span>👤 {r.contactName}</span>}
                  {r.ownerName && <span>· {r.ownerName}</span>}
                  {r.closedate && <span>· {r.closedate.slice(0,10)}</span>}
                  {r.cuotas > 1 && <span>· {r.cuotas} cuotas</span>}
                  {r.gateway && <span>· {r.gateway}</span>}
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
